import React, { useState, useEffect } from "react";
import moment from "moment";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartTooltip,
  ResponsiveContainer, Cell, Legend, ReferenceLine,
} from "recharts";
import {
  Calendar, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight,
  BarChart2, TableProperties, Wallet, PiggyBank, Percent, BadgeIndianRupee,
  CircleDollarSign, Layers, Receipt, Landmark, Pencil
} from "lucide-react";
import StyledTooltip from "../common/StyledTooltip";
import Loader from '../../../../presentationals/Loader/Loader.component'
import { saveMonthlyFinancialData } from "../API's/Actioncreator";
import Popup from '../../../../presentationals/Popup/Popup.component'

// ─── Global CSS ───────────────────────────────────────────────────────────────
const CSS = `
  @keyframes shimmer {
    0%   { background-position: -800px 0 }
    100% { background-position:  800px 0 }
  }
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(14px) }
    to   { opacity: 1; transform: translateY(0)    }
  }
  @keyframes fadeIn {
    from { opacity: 0 }
    to   { opacity: 1 }
  }
  @keyframes scaleIn {
    from { opacity: 0; transform: scale(0.96) }
    to   { opacity: 1; transform: scale(1)    }
  }

  .avp-kpi { transition: transform .2s, box-shadow .2s; cursor: default; }
  .avp-kpi:hover { transform: translateY(-3px); box-shadow: 0 10px 30px rgba(0,0,0,.10) !important; }

  .avp-tab { transition: all .18s; border: none; cursor: pointer; font-family: inherit; }
  .avp-tab:hover { filter: brightness(0.94); }

  .avp-tr { transition: background .15s; }
  .avp-tr:hover { background: #F8FAFC !important; }

  .avp-track {
    height: 7px; border-radius: 99px;
    background: #EEF2F7; overflow: hidden;
  }
  .avp-fill {
    height: 100%; border-radius: 99px;
    transition: width .8s cubic-bezier(.4,0,.2,1);
  }

  .editable-cell {
    position: relative;
    cursor: pointer;
    transition: background-color 0.15s ease;
  }
  .editable-cell:hover {
    background-color: #F1F5F9 !important;
  }
  .editable-cell .edit-icon {
    position: absolute;
    left: calc(100% + 6px);
    top: 50%;
    transform: translateY(-50%) scale(0.9);
    opacity: 0;
    transition: all 0.2s ease;
    color: #4F46E5;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    user-select: none;
  }
  .editable-cell:hover .edit-icon {
    opacity: 1;
    transform: translateY(-50%) scale(1.1);
  }
`;

// ─── Design tokens ────────────────────────────────────────────────────────────
const BRAND_A = "#3B5BDB";
const BRAND_B = "#6741D9";
const BRAND_C = "#9C36B5";
const BRAND_GR = `linear-gradient(135deg, ${BRAND_A} 0%, ${BRAND_B} 50%, ${BRAND_C} 100%)`;

const C_GREEN = "#10B981";
const C_RED = "#EF4444";
const C_AMBER = "#F59E0B";
const C_BLUE = "#3B82F6";
const C_VIOLET = "#7C3AED";
const C_TEAL = "#0D9488";
const C_ROSE = "#F43F5E";
const C_INDIGO = "#4F46E5";
const C_ORANGE = "#EA580C";

// chart colour set
const BAR_PROJ = "#A5B4FC";   // soft indigo  — projected
const BAR_ACT = "#6366F1";   // vivid indigo — actual revenue
const BAR_EXP = "#F472B6";   // pink         — expense bars
const BAR_PROFIT_POS = "#34D399"; // emerald
const BAR_PROFIT_NEG = "#FB7185"; // rose

const isLg = window.innerWidth > 1800;

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmt = (v) => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "number") return v.toLocaleString("en-IN");
  return String(v);
};

// Always renders ₹ and amount on the same line, never wraps
const Rs = ({ v, style = {} }) => (
  <span style={{ whiteSpace: "nowrap", ...style }}>₹&nbsp;{fmt(v)}</span>
);

const fmtShort = (v) => {
  const n = Number(v);
  if (!n && n !== 0) return "—";
  if (n >= 1_00_00_000) return `₹${(n / 1_00_00_000).toFixed(1)}Cr`;
  if (n >= 1_00_000) return `₹${(n / 1_00_000).toFixed(1)}L`;
  if (n >= 1_000) return `₹${(n / 1_000).toFixed(1)}K`;
  return `₹${n}`;
};

const pctAch = (actual, target) => {
  if (!target) return 0;
  return Math.min(Math.round((actual / target) * 100), 200);
};

const calcVar = (actual, projected) => {
  const act = Number(actual) || 0;
  const proj = Number(projected) || 0;
  const diff = act - proj;
  const pct = proj !== 0 ? (diff / Math.abs(proj)) * 100 : 0;
  return { diff, pct, isPos: diff >= 0 };
};

// ─── Skeleton ─────────────────────────────────────────────────────────────────
const Sk = ({ w, h, r = "7px", light = false, mx = "0", style = {} }) => (
  <div style={{
    width: w, height: h, borderRadius: r, flexShrink: 0,
    background: light
      ? "linear-gradient(90deg,#fff2 25%,#fff5 50%,#fff2 75%)"
      : "linear-gradient(90deg,#ECEEF2 25%,#E2E5EB 50%,#ECEEF2 75%)",
    backgroundSize: "800px 100%",
    animation: "shimmer 1.6s infinite linear",
    margin: `0 ${mx}`,
    ...style,
  }} />
);

const SkeletonView = () => (
  <div style={{ animation: "fadeIn .3s" }}>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "16px", marginBottom: "26px" }}>
      {[0, 1, 2, 3].map(i => (
        <div key={i} style={{ background: "#F8FAFC", border: "1px solid #E9EDF3", borderRadius: "14px", padding: "18px 20px" }}>
          <Sk w="56px" h="11px" style={{ marginBottom: "12px" }} />
          <Sk w="100px" h="26px" style={{ marginBottom: "9px" }} />
          <Sk w="74px" h="10px" />
        </div>
      ))}
    </div>
    <div style={{ background: "#F8FAFC", borderRadius: "14px", padding: "22px", marginBottom: "22px" }}>
      <Sk w="160px" h="15px" style={{ marginBottom: "18px" }} />
      <Sk w="100%" h="220px" r="10px" />
    </div>
    {[0, 1, 2, 3, 4].map(i => (
      <div key={i} style={{ display: "flex", gap: "14px", padding: "14px 2px", borderBottom: "1px solid #F1F5F9", alignItems: "center" }}>
        <Sk w={i % 2 === 0 ? "150px" : "120px"} h="13px" />
        <Sk w="80px" h="13px" style={{ marginLeft: "auto" }} />
        <Sk w="80px" h="13px" />
        <Sk w="90px" h="6px" r="99px" />
        <Sk w="72px" h="20px" r="99px" />
      </div>
    ))}
  </div>
);

// ─── KPI Card ─────────────────────────────────────────────────────────────────
const KpiCard = ({ label, value, sub, subColor = "#6B7280", valueColor = "#0A0A0A", accent, topBorderColor, icon: Icon, tooltip }) => {
  const inner = (
    <div className="avp-kpi" style={{
      background: "#fff",
      border: "1px solid #E5E7EB",
      borderRadius: "14px",
      borderTop: topBorderColor ? `3px solid ${topBorderColor}` : undefined,
      padding: isLg ? "22px 24px" : "16px 18px",
      boxShadow: "0 1px 4px rgba(0,0,0,.05)",
      display: "flex", flexDirection: "column", gap: "10px",
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: isLg ? "12px" : "10px", fontWeight: "700", color: "#8896AB", textTransform: "uppercase", letterSpacing: "0.07em" }}>
          {label}
        </span>
        {Icon && (
          <div style={{ width: "32px", height: "32px", borderRadius: "9px", background: accent ? `${accent}18` : "#F0F4FF", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon size={15} color={accent || C_BLUE} strokeWidth={2} style={{ width: "auto" }} />
          </div>
        )}
      </div>
      <div style={{ fontSize: isLg ? "28px" : "20px", fontWeight: "800", color: valueColor, lineHeight: 1, letterSpacing: "-0.5px" }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: isLg ? "12px" : "11px", color: subColor, fontWeight: "500", lineHeight: 1.3 }}>
          {sub}
        </div>
      )}
    </div>
  );
  // return tooltip
  //   ? <StyledTooltip showcustomVal={true} customVal={tooltip}>{inner}</StyledTooltip>
  //   : inner;
  return inner;
};

// ─── Custom recharts tooltip ──────────────────────────────────────────────────
const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "#1E293B", borderRadius: "12px", padding: "12px 16px",
      boxShadow: "0 12px 32px rgba(0,0,0,.25)", fontSize: isLg ? "13px" : "12px",
    }}>
      <p style={{ margin: "0 0 8px", fontWeight: "700", color: "#94A3B8", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.07em" }}>
        {label}
      </p>
      {payload.map((p, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: i < payload.length - 1 ? "5px" : 0 }}>
          <div style={{ width: "10px", height: "10px", borderRadius: "3px", background: p.fill, flexShrink: 0 }} />
          <span style={{ color: "#CBD5E1" }}>{p.name}:</span>
          <span style={{ fontWeight: "700", color: "#fff" }}>₹ {fmt(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

// ─── Custom legend ────────────────────────────────────────────────────────────
const ChartLegend = () => (
  <div style={{ display: "flex", gap: "20px", justifyContent: "center", marginBottom: "4px" }}>
    {[
      { color: BAR_PROJ, label: "Projected" },
      { color: BAR_ACT, label: "Actual Revenue" },
      { color: BAR_EXP, label: "Expense" },
    ].map(({ color, label }) => (
      <div key={label} style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#6B7280", fontWeight: "600" }}>
        <div style={{ width: "12px", height: "12px", borderRadius: "3px", background: color, flexShrink: 0 }} />
        {label}
      </div>
    ))}
  </div>
);

// ─── Progress bar ─────────────────────────────────────────────────────────────
const PBar = ({ pct, color }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: "110px" }}>
    <div className="avp-track" style={{ flex: 1 }}>
      <div className="avp-fill" style={{ width: `${Math.min(pct, 100)}%`, background: color }} />
    </div>
    <span style={{ fontSize: "13px", fontWeight: "700", color, minWidth: "34px" }}>
      {pct > 200 ? ">200" : pct}%
    </span>
  </div>
);

// ─── Variance chip ────────────────────────────────────────────────────────────
const VarChip = ({ v }) => {
  if (!v) return <span style={{ color: "#9CA3AF", fontSize: "13px" }}>—</span>;
  const color = v.isPos ? C_GREEN : C_RED;
  const bg = v.isPos ? "#ECFDF5" : "#FFF1F2";
  const border = v.isPos ? "#A7F3D0" : "#FECDD3";
  const sign = v.isPos ? "+" : "";
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: "3px",
      background: bg, color, border: `1px solid ${border}`,
      borderRadius: "999px", padding: "3px 10px",
      fontSize: "13px", fontWeight: "700", whiteSpace: "nowrap",
    }}>
      {v.isPos
        ? <ArrowUpRight size={11} strokeWidth={2.5} style={{ width: "auto" }} />
        : <ArrowDownRight size={11} strokeWidth={2.5} style={{ width: "auto" }} />
      }
      {sign}{fmtShort(Math.abs(v.diff))} ({sign}{v.pct.toFixed(1)}%)
    </span>
  );
};

// ─── Section divider row ──────────────────────────────────────────────────────
const SectionHdr = ({ label, icon: Icon, color }) => (
  <tr>
    <td colSpan={5} style={{
      padding: "10px 20px",
      background: `linear-gradient(90deg, ${color}18 0%, transparent 100%)`,
      borderTop: `1px solid ${color}33`,
      borderBottom: `1px solid ${color}22`,
    }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: "7px", fontSize: "10px", fontWeight: "800", color, textTransform: "uppercase", letterSpacing: "0.1em" }}>
        <Icon size={12} strokeWidth={2.5} style={{ width: "auto" }} />
        {label}
      </span>
    </td>
  </tr>
);

// ─── normalizeRows (generic fallback) ─────────────────────────────────────────
const normalizeRows = (payload) => {
  const data = payload?.data ?? payload;
  if (!data) return { columns: [], rows: [] };
  if (Array.isArray(data)) {
    const cols = Array.from(new Set(data.flatMap(r => r && typeof r === "object" ? Object.keys(r) : [])));
    return { columns: cols, rows: data };
  }
  if (typeof data === "object") {
    if (Array.isArray(data.rows)) {
      const cols = Array.isArray(data.columns) ? data.columns
        : Array.from(new Set(data.rows.flatMap(r => r && typeof r === "object" ? Object.keys(r) : [])));
      return { columns: cols, rows: data.rows };
    }
    return { columns: ["Metric", "Value"], rows: Object.entries(data).map(([k, v]) => ({ Metric: k, Value: v })) };
  }
  return { columns: ["Value"], rows: [{ Value: data }] };
};

// ═════════════════════════════════════════════════════════════════════════════
// Main
// ═════════════════════════════════════════════════════════════════════════════
export default function ActualVsProfitTable({ data, loading = false, error = null, selectedMonth, commonFilter, onSaveSuccess }) {
  const [tab, setTab] = useState("table");
  const [loader, setLoader] = useState(false)
    const [popup, setpopup] = useState({ popupStatus: "", show: false });

  const payload = data?.data ?? data;
  const isFinancial = payload && typeof payload === "object" && !Array.isArray(payload) &&
    ("manualExpense" in payload || "revenueInterest" in payload || "manualProvision" in payload);

  const { columns, rows } = normalizeRows(data);

  const subtitle =
    commonFilter?.fromDate && commonFilter?.toDate
      ? `${moment(commonFilter.fromDate).format("D MMM YYYY")} → ${moment(commonFilter.toDate).format("D MMM YYYY")}`
      : selectedMonth?.length ? selectedMonth.join(", ") : "";

  // ── Local State for Inline Editing ──────────────────────────────────────────
  const [values, setValues] = useState({
    projInterest: 0,
    actualInterest: 0,
    projPF: 0,
    actualPF: 0,
    projCharges: 0,
    actualCharges: 0,
    projExpense: 0,
    actualExpense: 0,
    projProvision: 0,
    actualProvision: 0,
    totalRevenue: 0,
    projRevTotal: 0,
    totalExpense: 0,
    totalExpenseActual: 0,
    profit: 0,
    profitProjected: 0,
  });

  const [editingCell, setEditingCell] = useState(null); // { rowId, colKey }
  const [tempValue, setTempValue] = useState("");

  const d = payload || {};

  useEffect(() => {
    if (payload && typeof payload === "object") {
      const projInterest = Number(payload.projectedInterest) || Number(payload.manualInterest) || 0;
      const actualInterest = Number(payload.actualInterest) || Number(payload.revenueInterest) || 0;
      const projPF = Number(payload.projectedPf) || Number(payload.manualProcessingFee) || Number(payload.revenueProcessingFee) || 0;
      const actualPF = Number(payload.actualPf) || Number(payload.revenueProcessingFee) || 0;
      const projCharges = Number(payload.projectedRevCharges) || Number(payload.manualCharges) || 0;
      const actualCharges = Number(payload.actualRevCharges) || Number(payload.revenueCharges) || 0;
      const projExpense = Number(payload.projectedExpense) || Number(payload.manualExpense) || 0;
      const actualExpense = Number(payload.actualExpense) || 0;
      const projProvision = Number(payload.projectedProvision) || Number(payload.manualProvision) || 0;
      const actualProvision = Number(payload.actualProvision) || 0;

      setValues({
        projInterest,
        actualInterest,
        projPF,
        actualPF,
        projCharges,
        actualCharges,
        projExpense,
        actualExpense,
        projProvision,
        actualProvision,
        totalRevenue: Number(payload.totalRevenue) || (actualInterest + actualPF + actualCharges),
        projRevTotal: Number(payload.projRevTotal) || (projInterest + projPF + projCharges),
        totalExpense: Number(payload.totalExpense) || (projExpense + projProvision),
        totalExpenseActual: Number(payload.totalExpenseActual) || (actualExpense + actualProvision),
        profit: Number(payload.netProfit) || ((actualInterest + actualPF + actualCharges) - (actualExpense + actualProvision)),
        profitProjected: Number(payload.profitProjected) || ((projInterest + projPF + projCharges) - (projExpense + projProvision)),
      });
    }
  }, [payload]);

  if (!loading && !isFinancial && !error) {
    return null;
  }

  const startEditing = (rowId, colKey, initialVal) => {
    setEditingCell({ rowId, colKey });
    setTempValue(initialVal === "—" || initialVal === null || initialVal === undefined ? "" : String(initialVal));
  };

  const saveEditing = (rowId, colKey) => {
    if (!editingCell) return;
    const numVal = tempValue === "" ? 0 : Number(tempValue);
    if (!isNaN(numVal)) {
      setValues(prev => {
        const next = { ...prev };
        if (colKey === "actual") {
          if (rowId === "interest") next.actualInterest = numVal;
          else if (rowId === "processing_fee") next.actualPF = numVal;
          else if (rowId === "charges") next.actualCharges = numVal;
          else if (rowId === "expense") next.actualExpense = numVal;
          else if (rowId === "provision") next.actualProvision = numVal;
        } else if (colKey === "projected") {
          if (rowId === "interest") next.projInterest = numVal;
          else if (rowId === "processing_fee") next.projPF = numVal;
          else if (rowId === "charges") next.projCharges = numVal;
          else if (rowId === "expense") next.projExpense = numVal;
          else if (rowId === "provision") next.projProvision = numVal;
        }

        // Recalculate totals locally upon editing
        next.totalRevenue = next.actualInterest + next.actualPF + next.actualCharges;
        next.projRevTotal = next.projInterest + next.projPF + next.projCharges;
        next.totalExpense = next.projExpense + next.projProvision;
        next.totalExpenseActual = next.actualExpense + next.actualProvision;
        next.profit = next.totalRevenue - next.totalExpenseActual;
        next.profitProjected = next.projRevTotal - next.totalExpense;

        return next;
      });
    }
    setEditingCell(null);
  };

  const handleKeyDown = (e, rowId, colKey) => {
    if (e.key === "Enter") {
      saveEditing(rowId, colKey);
    } else if (e.key === "Escape") {
      setEditingCell(null);
    }
  };

  const handleSave = () => {
    setLoader(true);

    const periods = selectedMonth?.length > 0
      ? selectedMonth
      : [new Date().toLocaleString("default", { month: "short", year: "numeric" })];

    const actualData = {
      projectedInterest: values.projInterest,
      projectedPf: values.projPF,
      actualInterest: values.actualInterest,
      actualPf: values.actualPF,
      actualRevCharges: values.actualCharges,
      actualExpense: values.actualExpense,
      actualProvision: values.actualProvision,

      // Legacy fallback keys
      revenueInterest: values.actualInterest,
      revenueProcessingFee: values.actualPF,
      revenueCharges: values.actualCharges,
      totalRevenue: values.totalRevenue,
      projRevTotal: values.projRevTotal,
      totalExpense: values.totalExpense,
      totalExpenseActual: values.totalExpenseActual,
      netProfit: values.profit,
      profitProjected: values.profitProjected,
      manualInterest: values.projInterest,
      manualProcessingFee: values.projPF
    };

    const savePromises = periods.map(period =>
      new Promise((resolve, reject) => {
        saveMonthlyFinancialData(
          period,
          values.projExpense,
          values.projProvision,
          values.projCharges,
          actualData,
          true,
          resolve,
          reject
        );
      })
    );

    Promise.all(savePromises)
      .then(responses => {
        setLoader(false);
        console.log("Table API Responses:", responses);
        // alert(`Data saved successfully for ${periods.length} month(s)!`);
        setpopup({ show: true, popupStatus: `Data saved successfully for ${periods.length} month(s)!` })
        removePopup()
        if (onSaveSuccess) onSaveSuccess();
      })
      .catch(error => {
        setLoader(false);
        console.error("Error saving data from table:", error);
        // alert("Error saving data. Please try again.");
        setpopup({ show: true, popupStatus: `Error saving data. Please try again.` })
      removePopup()
      });
  };

  const renderEditableCell = (rowId, colKey, value, isEditable) => {
    const isEditing = editingCell?.rowId === rowId && editingCell?.colKey === colKey;

    if (isEditable) {
      if (isEditing) {
        return (
          <td style={{
            textAlign: "center",
            padding: isLg ? "7px 18px" : "5px 14px", // Adjusted padding to maintain exact cell height (prevents layout shift)
            verticalAlign: "middle",
          }}>
            <div style={{
              display: "inline-flex",
              alignItems: "center",
              border: `1.5px solid #2563eb`,
              borderRadius: "6px",
              background: "#fff",
              padding: "0 6px",
              height: isLg ? "36px" : "32px",
              boxSizing: "border-box",
              boxShadow: "0 0 0 2px rgba(37,99,235,0.15)",
              margin: "0 auto",
            }}>
              <span style={{ fontSize: "14px", color: "#9CA3AF", fontWeight: "600", marginRight: "3px", userSelect: "none" }}>
                ₹
              </span>
              <input
                type="number"
                value={tempValue}
                onChange={(e) => setTempValue(e.target.value)}
                onBlur={() => saveEditing(rowId, colKey)}
                onKeyDown={(e) => handleKeyDown(e, rowId, colKey)}
                autoFocus
                style={{
                  width: isLg ? "105px" : "90px",
                  height: "100%",
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  fontSize: isLg ? "16px" : "15px",
                  fontWeight: "700",
                  color: "#111827",
                  padding: "0",
                  margin: "0",
                }}
              />
            </div>
          </td>
        );
      } else {
        const displayVal = value !== null && value !== undefined && value !== "" ? <Rs v={value} /> : <span style={{ color: "#9CA3AF" }}>—</span>;
        return (
          <td
            className="editable-cell"
            style={{
              textAlign: "center",
              padding: isLg ? "13px 18px" : "11px 14px",
              fontWeight: colKey === "actual" ? "600" : "500",
              color: colKey === "actual" ? "#111827" : "#6B7280",
              fontSize: isLg ? "16px" : "15px",
              verticalAlign: "middle",
            }}
            onClick={() => startEditing(rowId, colKey, value)}
            title="Click to edit"
          >
            <span style={{ position: "relative", display: "inline-block" }}>
              {displayVal}
              <Pencil className="edit-icon" size={13} />
            </span>
          </td>
        );
      }
    }

    return (
      <td style={{
        textAlign: "center",
        padding: isLg ? "13px 18px" : "11px 14px",
        color: colKey === "actual" ? "#111827" : "#6B7280",
        fontWeight: colKey === "actual" ? "600" : "500",
        fontSize: isLg ? "16px" : "15px",
        verticalAlign: "middle",
      }}>
        {value !== null && value !== undefined && value !== "" ? <Rs v={value} /> : <span style={{ color: "#9CA3AF" }}>—</span>}
      </td>
    );
  };

  // ── Calculated derived values ────────────────────────────────────────────────
  const totalRevenue = values.totalRevenue;
  const projRevTotal = values.projRevTotal;
  const totalExpense = values.totalExpense;
  const totalExpenseActual = values.totalExpenseActual;
  const profit = values.profit;
  const profitProjected = values.profitProjected;
  const isProfit = profit >= 0;
  const margin = totalRevenue > 0 ? ((profit / totalRevenue) * 100).toFixed(1) : null;

  // ── Chart rows ───────────────────────────────────────────────────────────
  const chartRows = [
    { name: "Interest", Projected: values.projInterest, Actual: values.actualInterest, type: "rev" },
    { name: "PF & Charges", Projected: values.projPF + values.projCharges, Actual: values.actualPF + values.actualCharges, type: "rev" },
    { name: "Expense", Projected: totalExpense, Actual: totalExpenseActual, type: "exp" },
  ];

  // ── Table row definitions ────────────────────────────────────────────────
  const tableRows = [
    // Revenue section
    { _section: "Revenue", _sectionColor: C_INDIGO, _sectionIcon: ArrowUpRight },
    {
      id: "interest",
      label: "Interest Income",
      projected: values.projInterest,
      actual: values.actualInterest,
      dot: C_TEAL,
      badge: { label: "Lending", bg: "#CCFBF1", color: "#0F766E" },
      editableProjected: true,
      editableActual: true,
    },
    {
      id: "processing_fee",
      label: "Processing Fee",
      projected: values.projPF,
      actual: values.actualPF,
      dot: C_AMBER,
      badge: { label: "Upfront", bg: "#FEF3C7", color: "#92400E" },
      editableProjected: true,
      editableActual: true,
    },
    {
      id: "charges",
      label: "Charges",
      projected: values.projCharges,
      actual: values.actualCharges,
      dot: C_ORANGE,
      badge: { label: "Upfront", bg: "#FFE2E2", color: "#9A3412" },
      editableProjected: true,
      editableActual: true,
    },
    {
      id: "total_revenue",
      label: "Total Revenue",
      projected: projRevTotal,
      actual: totalRevenue,
      isTotal: true,
      totalColor: C_INDIGO,
    },
    // Expense section
    { _section: "Expenses", _sectionColor: C_ROSE, _sectionIcon: ArrowDownRight },
    {
      id: "expense",
      label: "Expenses",
      projected: values.projExpense,
      actual: values.actualExpense,
      dot: C_ROSE,
      badge: { label: "Manual", bg: "#FFF1F2", color: "#9F1239" },
      tooltip: "Manual entry — expenses value",
      isManual: true,
      editableProjected: true,
      editableActual: true,
    },
    {
      id: "provision",
      label: "Provision",
      projected: values.projProvision,
      actual: values.actualProvision,
      dot: C_VIOLET,
      badge: { label: "Manual", bg: "#EDE9FE", color: "#5B21B6" },
      tooltip: "Manual entry — provision value",
      isManual: true,
      editableProjected: true,
      editableActual: true,
    },
    {
      id: "total_expenses",
      label: "Total Expenses",
      projected: totalExpense,
      actual: totalExpenseActual,
      isTotal: true,
      totalColor: C_ROSE,
    },
    // P&L
    { _divider: true },
    {
      id: "net_profit",
      label: "Net Profit / Loss",
      projected: profitProjected,
      actual: profit,
      isPnl: true,
    },
  ];

  // ── renderFinancial ──────────────────────────────────────────────────────
  const renderFinancial = () => (
    <div style={{ animation: "fadeUp .35s ease-out" }}>

      {/* ── KPI cards ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: "14px", marginBottom: "26px" }}>
        <KpiCard
          label="Net P&L"
          value={<Rs v={profit} />}
          sub={isProfit ? "▲ Surplus this period" : "▼ Deficit this period"}
          valueColor={isProfit ? C_GREEN : C_RED}
          subColor={isProfit ? C_GREEN : C_RED}
          accent={isProfit ? C_GREEN : C_RED}
          topBorderColor={isProfit ? C_GREEN : C_RED}
          icon={isProfit ? TrendingUp : TrendingDown}
        />
        <KpiCard
          label="Actual Revenue"
          value={<Rs v={totalRevenue} />}
          sub={<>Target <Rs v={projRevTotal} /></>}
          accent={C_INDIGO}
          topBorderColor={C_INDIGO}
          icon={BadgeIndianRupee}
        />
        <KpiCard
          label="Total Expenses"
          value={<Rs v={totalExpense} />}
          sub={<>Exp <Rs v={values.projExpense} /> + Prov <Rs v={values.projProvision} /></>}
          accent={C_ROSE}
          topBorderColor={C_ROSE}
          icon={Wallet}
          tooltip={`Operating Expense ₹${fmt(values.projExpense)} + Provision ₹${fmt(values.projProvision)}`}
        />
        <KpiCard
          label="Profit Margin"
          value={margin !== null ? `${margin}%` : "—"}
          sub="Net ÷ Revenue"
          accent={C_AMBER}
          topBorderColor={C_AMBER}
          icon={Percent}
        />
      </div>

      {/* ── Hero P&L strip ── */}
      <div style={{
        background: isProfit
          ? "linear-gradient(135deg,#064E3B 0%,#059669 60%,#34D399 100%)"
          : "linear-gradient(135deg,#7F1D1D 0%,#DC2626 60%,#F87171 100%)",
        borderRadius: "16px",
        padding: isLg ? "28px 36px" : "20px 24px",
        marginBottom: "26px",
        position: "relative", overflow: "hidden",
      }}>
        {/* subtle grid watermark */}
        <svg style={{ position: "absolute", inset: 0, opacity: 0.07 }} width="100%" height="100%">
          <defs>
            <pattern id="g" width="32" height="32" patternUnits="userSpaceOnUse">
              <path d="M 32 0 L 0 0 0 32" fill="none" stroke="white" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#g)" />
        </svg>

        <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "20px" }}>
          <div>
            <p style={{ margin: "0 0 4px", fontSize: isLg ? "13px" : "11px", color: "rgba(255,255,255,.7)", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.07em" }}>
              Period Net Profit / Loss
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
              <span style={{ fontSize: isLg ? "40px" : "28px", fontWeight: "800", color: "#fff", letterSpacing: "-1.5px", lineHeight: 1, whiteSpace: "nowrap" }}>
                <Rs v={profit} />
              </span>
              <span style={{ background: "rgba(255,255,255,.18)", backdropFilter: "blur(4px)", color: "#fff", borderRadius: "8px", padding: "4px 12px", fontSize: isLg ? "14px" : "12px", fontWeight: "700" }}>
                {isProfit ? "▲ Surplus" : "▼ Deficit"}
              </span>
            </div>
          </div>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            {[
              { label: "Revenue", value: <Rs v={totalRevenue} />, icon: CircleDollarSign },
              { label: "Expenses", value: <Rs v={totalExpense} />, icon: Layers },
              { label: "Margin", value: margin ? `${margin}%` : "—", icon: Percent },
            ].map(({ label, value, icon: Ic }) => (
              <div key={label} style={{ background: "rgba(255,255,255,.12)", border: "1px solid rgba(255,255,255,.18)", borderRadius: "12px", padding: "12px 20px", textAlign: "center", minWidth: "90px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "5px", marginBottom: "4px" }}>
                  <Ic size={12} color="rgba(255,255,255,.65)" strokeWidth={2} style={{ width: "auto" }} />
                  <span style={{ fontSize: "10px", color: "rgba(255,255,255,.65)", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.07em" }}>{label}</span>
                </div>
                <span style={{ fontSize: isLg ? "18px" : "15px", fontWeight: "700", color: "#fff" }}>{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", gap: "6px", marginBottom: "20px", justifyContent: 'flex-end' }}>
        <button className="btn btn-primary" onClick={() => handleSave()}>Save</button>
      </div>

      {/* ── Tab switcher ── */}
      {/* <div style={{ display: "flex", gap: "6px", marginBottom: "20px" }}>
        {[
          { id: "chart", label: "Chart View", Icon: BarChart2 },
          { id: "table", label: "Table View", Icon: TableProperties },
        ].map(({ id, label, Icon }) => {
          const active = tab === id;
          return (
            <button key={id} className="avp-tab" onClick={() => setTab(id)} style={{
              padding: "8px 18px",
              borderRadius: "9px",
              fontSize: isLg ? "13px" : "12px",
              fontWeight: "700",
              background: active ? BRAND_GR : "#F1F5F9",
              color: active ? "#fff" : "#64748B",
              display: "inline-flex", alignItems: "center", gap: "7px",
              boxShadow: active ? "0 3px 10px rgba(99,102,241,.35)" : "none",
            }}>
              <Icon size={13} strokeWidth={2.2} style={{ width: "auto" }} />
              {label}
            </button>
          );
        })}
      </div> */}

      {/* ═══ CHART TAB ══════════════════════════════════════════════════════ */}
      {tab === "chart" && (
        <div style={{
          background: "#FAFBFE",
          border: "1px solid #E5E7EB",
          borderRadius: "14px",
          padding: isLg ? "24px 28px 18px" : "18px 20px 14px",
          animation: "scaleIn .25s ease-out",
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px", flexWrap: "wrap", gap: "10px" }}>
            <span style={{ fontSize: isLg ? "15px" : "13px", fontWeight: "700", color: "#1E293B" }}>
              Projected vs Actual — Visual Breakdown
            </span>
            <ChartLegend />
          </div>

          <ResponsiveContainer width="100%" height={isLg ? 300 : 230}>
            <BarChart data={chartRows} barGap={3} barCategoryGap="28%" margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="gradProj" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={BAR_PROJ} stopOpacity={1} />
                  <stop offset="100%" stopColor={BAR_PROJ} stopOpacity={0.6} />
                </linearGradient>
                <linearGradient id="gradAct" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={BAR_ACT} stopOpacity={1} />
                  <stop offset="100%" stopColor={BAR_ACT} stopOpacity={0.75} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis
                dataKey="name"
                tick={{ fontSize: isLg ? 12 : 10, fill: "#6B7280", fontWeight: 600 }}
                axisLine={false} tickLine={false}
              />
              <YAxis
                tickFormatter={fmtShort}
                tick={{ fontSize: isLg ? 11 : 10, fill: "#94A3B8" }}
                axisLine={false} tickLine={false}
                width={isLg ? 72 : 60}
              />
              <RechartTooltip content={<ChartTip />} cursor={{ fill: "rgba(99,102,241,.05)", radius: 6 }} />
              <ReferenceLine y={0} stroke="#CBD5E1" strokeDasharray="4 2" />

              <Bar dataKey="Projected" name="Projected" radius={[6, 6, 0, 0]} maxBarSize={32}>
                {chartRows.map((r, i) => (
                  <Cell key={i} fill={
                    r.type === "exp" ? "#FBBF24"
                      : r.type === "profit" ? (r.Projected >= 0 ? "#86EFAC" : "#FCA5A5")
                        : "url(#gradProj)"
                  } />
                ))}
              </Bar>

              <Bar dataKey="Actual" name="Actual" radius={[6, 6, 0, 0]} maxBarSize={32}>
                {chartRows.map((r, i) => (
                  <Cell key={i} fill={
                    r.type === "exp" ? BAR_EXP
                      : r.type === "profit" ? (r.Actual >= 0 ? BAR_PROFIT_POS : BAR_PROFIT_NEG)
                        : "url(#gradAct)"
                  } />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          {/* Achievement summary pills */}
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "18px", paddingTop: "16px", borderTop: "1px solid #E9EDF3" }}>
            {[
              { label: "Interest Achievement", pct: pctAch(values.actualInterest, values.projInterest) },
              { label: "PF & Chg Achievement", pct: pctAch(values.actualPF + values.actualCharges, values.projPF + values.projCharges) },
            ].map(({ label, pct }) => {
              const color = pct >= 100 ? C_GREEN : pct >= 70 ? C_AMBER : C_RED;
              return (
                <div key={label} style={{ flex: "1 1 160px", background: "#fff", border: "1px solid #E5E7EB", borderRadius: "10px", padding: "10px 14px" }}>
                  <div style={{ fontSize: "11px", color: "#6B7280", fontWeight: "600", marginBottom: "7px" }}>{label}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <div style={{ flex: 1, height: "7px", borderRadius: "99px", background: "#EEF2F7", overflow: "hidden" }}>
                      <div style={{ height: "100%", borderRadius: "99px", background: color, width: `${Math.min(pct, 100)}%`, transition: "width .8s" }} />
                    </div>
                    <span style={{ fontSize: "12px", fontWeight: "800", color }}>{pct}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ═══ TABLE TAB ══════════════════════════════════════════════════════ */}
      {tab === "table" && (
        <div style={{ animation: "scaleIn .25s ease-out", overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "640px" }}>
            <thead>
              <tr style={{ background: "#F1F5F9", borderBottom: "2px solid #E2E8F0" }}>
                {["Metric", "Projected", "Actual", "Achievement", "Variance"].map((h, i) => (
                  <th key={h} style={{
                    padding: isLg ? "13px 18px" : "11px 14px",
                    textAlign: i === 0 ? "left" : i === 3 ? "left" : "center",
                    fontSize: isLg ? "13px" : "11px",
                    fontWeight: "800", color: "#475569",
                    textTransform: "uppercase", letterSpacing: "0.08em",
                    whiteSpace: "nowrap",
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row, idx) => {
                // section header
                if (row._section) {
                  return <SectionHdr key={idx} label={row._section} icon={row._sectionIcon} color={row._sectionColor} />;
                }
                // divider
                if (row._divider) {
                  return (
                    <tr key={idx}>
                      <td colSpan={5} style={{ height: "6px", background: "linear-gradient(90deg,#6366F115,transparent)" }} />
                    </tr>
                  );
                }

                const v = calcVar(row.actual, row.projected);
                const pct = pctAch(row.actual, row.projected);
                const pctColor = pct >= 100 ? C_GREEN : pct >= 70 ? C_AMBER : C_RED;

                // P&L row
                if (row.isPnl) {
                  return (
                    <tr key={idx} style={{
                      background: isProfit ? "#ECFDF5" : "#FFF1F2",
                      borderTop: `2px solid ${isProfit ? "#6EE7B7" : "#FECDD3"}`,
                      borderBottom: `2px solid ${isProfit ? "#6EE7B7" : "#FECDD3"}`,
                    }}>
                      <td style={{ padding: isLg ? "16px 18px" : "13px 14px", fontWeight: "800", fontSize: isLg ? "18px" : "16px", color: isProfit ? "#064E3B" : "#7F1D1D" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          {isProfit
                            ? <TrendingUp size={17} color={C_GREEN} strokeWidth={2.5} style={{ width: "auto" }} />
                            : <TrendingDown size={17} color={C_RED} strokeWidth={2.5} style={{ width: "auto" }} />
                          }
                          {row.label}
                        </div>
                      </td>
                      <td style={{ textAlign: "center", padding: isLg ? "16px 18px" : "13px 14px", color: "#6B7280", fontWeight: "600", fontSize: isLg ? "16px" : "15px" }}>
                        <Rs v={row.projected} />
                      </td>
                      <td style={{ textAlign: "center", padding: isLg ? "16px 18px" : "13px 14px", fontWeight: "800", fontSize: isLg ? "18px" : "16px", color: isProfit ? "#059669" : "#DC2626" }}>
                        <Rs v={row.actual} />
                      </td>
                      <td style={{ padding: isLg ? "16px 18px" : "13px 14px" }}>—</td>
                      <td style={{ textAlign: "center", padding: isLg ? "16px 18px" : "13px 14px" }}>
                        <VarChip v={v} />
                      </td>
                    </tr>
                  );
                }

                // total row
                if (row.isTotal) {
                  const hasActual = row.actual !== null && row.actual !== undefined && row.actual !== "";
                  return (
                    <tr key={idx} className="avp-tr" style={{
                      background: `${row.totalColor}0A`,
                      borderTop: `1.5px solid ${row.totalColor}33`,
                      borderBottom: `1.5px solid ${row.totalColor}22`,
                    }}>
                      <td style={{ padding: isLg ? "14px 18px" : "12px 14px", fontWeight: "700", fontSize: isLg ? "16px" : "15px", color: row.totalColor }}>
                        {row.label}
                      </td>
                      <td style={{ textAlign: "center", padding: isLg ? "14px 18px" : "12px 14px", fontWeight: "700", color: "#374151", fontSize: isLg ? "16px" : "15px" }}>
                        <Rs v={row.projected} />
                      </td>
                      <td style={{ textAlign: "center", padding: isLg ? "14px 18px" : "12px 14px", fontWeight: "700", color: row.totalColor, fontSize: isLg ? "16px" : "15px" }}>
                        {hasActual ? <Rs v={row.actual} /> : <span style={{ color: "#9CA3AF", fontWeight: "500" }}>—</span>}
                      </td>
                      <td style={{ padding: isLg ? "14px 18px" : "12px 14px" }}>
                        {hasActual ? <PBar pct={pct} color={pctColor} /> : <span style={{ fontSize: "13px", color: "#9CA3AF" }}>—</span>}
                      </td>
                      <td style={{ textAlign: "center", padding: isLg ? "14px 18px" : "12px 14px" }}>
                        <VarChip v={v} />
                      </td>
                    </tr>
                  );
                }

                // normal data row (revenue + expense lines)
                const hasActual = row.actual !== null && row.actual !== undefined && row.actual !== "";
                const dataRow = (
                  <tr className="avp-tr" key={idx} style={{ borderBottom: "1px solid #F1F5F9" }}>
                    <td style={{ padding: isLg ? "13px 18px 13px 30px" : "11px 14px 11px 26px", color: "#374151", fontWeight: "500", fontSize: isLg ? "16px" : "15px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        {row.dot && <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: row.dot, flexShrink: 0 }} />}
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            {row.tooltip ? (
                              // <StyledTooltip showcustomVal={true} customVal={row.tooltip}>
                                <span style={{ borderBottom: "1px dashed #CBD5E1", cursor: "default" }}>{row.label}</span>
                              // </StyledTooltip>
                            ) : <span>{row.label}</span>}
                            {row.badge && (
                              <span style={{
                                fontSize: "11px", fontWeight: "700",
                                background: row.badge.bg || "#F1F5F9",
                                color: row.badge.color || "#6B7280",
                                borderRadius: "999px", padding: "2px 8px",
                                textTransform: "uppercase", letterSpacing: "0.05em",
                              }}>
                                {row.badge.label}
                              </span>
                            )}
                          </div>
                          {row.sub && <div style={{ fontSize: "13px", color: "#94A3B8", marginTop: "2px" }}>{row.sub}</div>}
                        </div>
                      </div>
                    </td>
                    {renderEditableCell(row.id, "projected", row.projected, row.editableProjected)}
                    {renderEditableCell(row.id, "actual", row.actual, row.editableActual)}
                    <td style={{ padding: isLg ? "13px 18px" : "11px 14px" }}>
                      {row.isManual ? (
                        <span style={{ fontSize: "13px", color: "#9CA3AF" }}>—</span>
                      ) : row.projected > 0 ? (
                        <PBar pct={pct} color={pctColor} />
                      ) : (
                        <span style={{ fontSize: "13px", color: "#9CA3AF" }}>—</span>
                      )}
                    </td>
                    <td style={{ textAlign: "center", padding: isLg ? "13px 18px" : "11px 14px" }}>
                      <VarChip v={v} />
                    </td>
                  </tr>
                );

                return dataRow;
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // ── generic fallback ──────────────────────────────────────────────────────
  const renderGeneric = () => (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: isLg ? "14px" : "13px" }}>
      <thead>
        <tr style={{ background: "#F9FAFB", borderBottom: "2px solid #E2E8F0" }}>
          {(columns.length ? columns : ["—"]).map((col, i) => (
            <th key={col} style={{
              padding: "12px 16px", textAlign: i === 0 ? "left" : "center",
              fontWeight: "700", color: "#374151", fontSize: "11px",
              textTransform: "uppercase", letterSpacing: "0.07em",
            }}>
              {col}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows?.length ? rows.map((row, idx) => (
          <tr key={idx} className="avp-tr" style={{ borderBottom: "0.8px solid #F3F4F6" }}>
            {columns.map((col, i) => (
              <td key={`${idx}-${col}`} style={{ padding: "12px 16px", textAlign: i === 0 ? "left" : "center", color: "#374151" }}>
                {fmt(row?.[col])}
              </td>
            ))}
          </tr>
        )) : (
          <tr>
            <td colSpan={Math.max(columns.length, 1)} style={{ padding: "56px 0", textAlign: "center", color: "#94A3B8", fontSize: "15px", fontWeight: "500" }}>
              No data found for the selected period.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );

    const closePopup = () => { setpopup({ show: false, popupStatus: "" }) }
  const removePopup = () => { setTimeout(() => { closePopup() }, 5000) }

  // ── Shell ─────────────────────────────────────────────────────────────────
  return (
    <>
      {loader ? <Loader /> : ""}
      {
        popup?.show ?
          <Popup popupStatus={popup?.popupStatus} closePopup={closePopup} />
          : ""}
      <style>{CSS}</style>
      <div style={{
        background: "#fff",
        border: "1px solid #E5E7EB",
        borderRadius: "16px",
        padding: isLg ? "28px 32px" : "20px 22px",
        marginTop: "20px",
        boxShadow: "0 2px 8px rgba(0,0,0,.06), 0 1px 2px rgba(0,0,0,.04)",
        overflowX: "auto",
      }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px", marginBottom: "24px", flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: isLg ? "22px" : "18px", fontWeight: "800", color: "#0A0A0A", letterSpacing: "-0.3px" }}>
              Financial Profit Statement
            </div>
            {subtitle && (
              <div style={{ fontSize: "13px", color: "#6B7280", marginTop: "5px", display: "inline-flex", alignItems: "center", gap: "5px", fontWeight: "500" }}>
                <Calendar size={13} strokeWidth={2} style={{ width: "auto" }} color="#9CA3AF" />
                {subtitle}
              </div>
            )}
          </div>
          <div style={{
            background: BRAND_GR, color: "#fff",
            borderRadius: "999px", padding: "5px 18px",
            fontSize: "12px", fontWeight: "700",
            boxShadow: "0 3px 12px rgba(99,66,217,.35)",
            whiteSpace: "nowrap",
          }}>
            Actual vs Projected
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{ padding: "12px 16px", borderRadius: "10px", marginBottom: "18px", background: "#FFF1F2", border: "1px solid #FECDD3", color: "#9F1239", fontWeight: "600", fontSize: "13px" }}>
            ⚠️ Failed to load financial profit data. Please refresh.
          </div>
        )}

        {loading ? <SkeletonView /> : isFinancial ? renderFinancial() : renderGeneric()}
      </div>
    </>
  );
}